// Snag collection page: the "In action" switch, View controls, instant filters and sort,
// card swatches, the featured video, and quick add. Markup: sections/snag-collection.liquid,
// snippets/snag-card.liquid. Filters and sort work on the products of the current page.

const store = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch { /* private mode: not saved */ }
  },
};

const SWATCH_OPTIONS = ['Character', 'Color', 'Colour', 'Design', 'Print', 'Model', 'Pattern'];
const TIER_OPTIONS = ['Bundle', 'Pack', 'Kit', 'Set'];
const CHOOSE_OPTIONS = ['Size'];
const shortLabel = (value) => String(value).split(' (')[0];
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function formatMoney(cents, format) {
  const fmt = format || '${{amount}}';
  const value = (Number(cents) || 0) / 100;
  const withSep = (n, dec, thousands, decimal) => {
    const [whole, frac] = n.toFixed(dec).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return withSep(value, 0, ',', '.');
      case 'amount_with_comma_separator': return withSep(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withSep(value, 0, '.', ',');
      default: return withSep(value, 2, ',', '.');
    }
  });
}

class SnagPlp extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.grid = this.querySelector('[data-grid]');
    if (!this.grid) return;
    this.moneyFormat = this.dataset.moneyFormat;
    this.creditPercent = Number(this.dataset.creditPercent) || 0;
    this.filters = new Map(); // option name -> Set of values ("Price" holds "min-max" in cents)
    this.sort = 'featured';

    this.#dropExpired();
    this.items = [...this.grid.children];
    for (const li of this.items) {
      if (li.dataset.item !== 'product') continue;
      const json = li.querySelector('[data-product-json]');
      try { li.snagData = JSON.parse(json.textContent); } catch { li.snagData = null; }
    }

    this.#initView();
    this.#initColumns();
    this.#initFilters();
    this.#initSort();
    this.#initCards();
    this.#initVideo();
    this.#initQuickAdd();
    this.#readUrl();
    this.#render();
  }

  /* ── dated editorial cards (pages are cached, so check again here) ── */
  #dropExpired() {
    const now = Date.now() / 1000;
    for (const li of this.querySelectorAll('[data-until]')) {
      if (now > Number(li.dataset.until)) li.remove();
    }
  }

  /* ── Product / In action ── */
  #initView() {
    const toggle = this.querySelector('[data-mode-toggle]');
    const saved = store.get('snag:plp:view');
    this.#setView(saved === 'action' || saved === 'studio' ? saved : this.dataset.defaultView || 'studio');
    toggle?.addEventListener('click', () => {
      const next = this.dataset.view === 'action' ? 'studio' : 'action';
      this.#setView(next);
      store.set('snag:plp:view', next);
    });
  }

  #setView(view) {
    this.dataset.view = view;
    this.querySelector('[data-mode-toggle]')?.setAttribute('aria-checked', String(view === 'action'));
  }

  /* ── View 2|4 (desktop) and 1|2 (mobile) ── */
  #initColumns() {
    const d = store.get('snag:plp:cols-d');
    const m = store.get('snag:plp:cols-m');
    if (d === '2' || d === '4') this.dataset.colsD = d;
    if (m === '1' || m === '2') this.dataset.colsM = m;
    for (const button of this.querySelectorAll('.snag-view button')) {
      button.addEventListener('click', () => {
        if (button.dataset.colsD) {
          this.dataset.colsD = button.dataset.colsD;
          store.set('snag:plp:cols-d', button.dataset.colsD);
        } else {
          this.dataset.colsM = button.dataset.colsM;
          store.set('snag:plp:cols-m', button.dataset.colsM);
        }
        this.#syncColumns();
      });
    }
    this.#syncColumns();
  }

  #syncColumns() {
    for (const button of this.querySelectorAll('.snag-view button')) {
      const on = button.dataset.colsD ? button.dataset.colsD === this.dataset.colsD : button.dataset.colsM === this.dataset.colsM;
      button.setAttribute('aria-pressed', String(on));
    }
  }

  /* ── filters ── */
  #initFilters() {
    this.panel = this.querySelector('[data-popover]');
    this.drawer = this.querySelector('[data-drawer]');

    // Price ranges nobody's products fall into are not offered.
    for (const label of this.querySelectorAll('[data-price-range]')) {
      const [lo, hi] = label.dataset.priceRange.split('-').map(Number);
      const any = this.#products().some((li) => li.snagData?.variants.some((v) => v.a && v.p >= lo && v.p < hi));
      if (!any) label.hidden = true;
    }
    const priceGroups = [...this.querySelectorAll('[data-fgroup="Price"]')];
    // Both copies (pill panel and drawer) are counted, so 2 ranges on offer means 4 labels.
    const priceUseful = this.querySelectorAll('.snag-popover [data-price-range]:not([hidden])').length >= 2;
    if (!priceUseful) {
      priceGroups.forEach((g) => g.remove());
      this.querySelector('[data-pill="Price"]')?.remove();
    }

    this.addEventListener('change', (event) => {
      const input = event.target.closest('input[data-f]');
      if (!input) return;
      const name = input.dataset.f;
      const set = this.filters.get(name) || new Set();
      if (input.checked) set.add(input.value); else set.delete(input.value);
      if (set.size) this.filters.set(name, set); else this.filters.delete(name);
      this.#render();
    });

    for (const pill of this.querySelectorAll('[data-pill]')) {
      pill.addEventListener('click', () => {
        if (pill.getAttribute('aria-expanded') === 'true') this.#closePopover();
        else this.#openPopover(pill);
      });
    }
    this.panel?.addEventListener('click', (event) => {
      if (event.target.closest('[data-close-pop]')) this.#closePopover();
    });
    // A click anywhere else closes the panel. No stopPropagation, so the panel's own
    // Clear button still reaches the section's click handler.
    document.addEventListener('click', (event) => {
      if (!this.panel || this.panel.hidden) return;
      if (this.panel.contains(event.target) || event.target.closest('[data-pill]')) return;
      this.#closePopover();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closePopover(true);
    });

    this.querySelector('[data-open-drawer]')?.addEventListener('click', () => {
      this.#closePopover();
      this.drawer?.showModal();
    });
    for (const button of this.querySelectorAll('[data-close-drawer]')) {
      button.addEventListener('click', () => this.drawer?.close());
    }
    this.drawer?.addEventListener('click', (event) => {
      if (event.target === this.drawer) this.drawer.close(); // backdrop
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-clear]')) {
        this.filters.clear();
        this.#render();
      }
      const chip = event.target.closest('[data-chip]');
      if (chip) {
        const set = this.filters.get(chip.dataset.chip);
        set?.delete(chip.dataset.value);
        if (set && !set.size) this.filters.delete(chip.dataset.chip);
        this.#render();
      }
    });
  }

  #openPopover(pill) {
    if (!this.panel) return;
    this.#closePopover();
    const name = pill.dataset.pill;
    for (const group of this.panel.querySelectorAll('.snag-fgroup')) {
      group.classList.toggle('is-open', group.dataset.fgroup === name);
    }
    this.panel.hidden = false;
    pill.setAttribute('aria-expanded', 'true');
    this.openPill = pill;
    if (window.matchMedia('(min-width: 750px)').matches) {
      const bar = pill.closest('.snag-bar');
      const left = pill.getBoundingClientRect().left - bar.getBoundingClientRect().left;
      this.panel.style.left = `${Math.max(0, Math.min(left, bar.clientWidth - this.panel.offsetWidth))}px`;
    } else {
      this.panel.style.left = '';
    }
    this.panel.querySelector('.snag-fgroup.is-open input')?.focus({ preventScroll: true });
  }

  #closePopover(returnFocus = false) {
    if (!this.panel || this.panel.hidden) return;
    this.panel.hidden = true;
    this.openPill?.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.openPill?.focus();
    this.openPill = null;
  }

  #products() {
    return this.items.filter((li) => li.dataset.item === 'product');
  }

  // Does this product have an in-stock variant that satisfies every active filter?
  // Values inside one filter are OR, filters are AND, all on the same variant.
  #matches(li, filters = this.filters) {
    const data = li.snagData;
    if (!data) return true;
    const optionFilters = [...filters].filter(([name, set]) => name !== 'Price' && set.size);
    const prices = filters.get('Price');
    for (const [name] of optionFilters) {
      if (!data.options.includes(name)) return false;
    }
    return data.variants.some((v) => {
      if (!v.a) return false;
      for (const [name, set] of optionFilters) {
        if (!set.has(v.o[data.options.indexOf(name)])) return false;
      }
      if (prices && prices.size) {
        return [...prices].some((range) => {
          const [lo, hi] = range.split('-').map(Number);
          return v.p >= lo && v.p < hi;
        });
      }
      return true;
    });
  }

  #render() {
    const active = [...this.filters.values()].some((set) => set.size);
    let shown = 0;
    for (const li of this.items) {
      if (li.dataset.item === 'editorial') {
        li.hidden = active; // pure results while filtering
        continue;
      }
      const ok = this.#matches(li);
      li.hidden = !ok;
      if (ok) shown += 1;
      this.#showFilteredLook(li);
    }

    // Keep every copy of each checkbox (pill panel and drawer) in step.
    for (const input of this.querySelectorAll('input[data-f]')) {
      input.checked = Boolean(this.filters.get(input.dataset.f)?.has(input.value));
      const trial = new Map(this.filters);
      trial.set(input.dataset.f, new Set([input.value]));
      const count = this.#products().filter((li) => this.#matches(li, trial)).length;
      input.closest('.snag-opt')?.classList.toggle('is-none', count === 0 && !input.checked);
    }
    for (const pill of this.querySelectorAll('[data-pill]')) {
      const n = this.filters.get(pill.dataset.pill)?.size || 0;
      pill.classList.toggle('is-active', n > 0);
      const badge = pill.querySelector('[data-pill-n]');
      if (badge) badge.textContent = n ? String(n) : '';
    }

    const label = `${shown} ${shown === 1 ? 'product' : 'products'}`;
    const shownEl = this.querySelector('[data-shown]');
    if (shownEl) shownEl.textContent = label;
    for (const button of this.querySelectorAll('[data-drawer-show], [data-pop-show]')) {
      button.textContent = shown ? `Show ${label}` : 'No matches';
    }
    const empty = this.querySelector('[data-empty]');
    if (empty) empty.hidden = shown > 0;

    this.#renderChips();
    this.#applySort();
    this.#writeUrl();
  }

  // With exactly one Character (or Color...) chosen, the card shows that design.
  #showFilteredLook(li) {
    const data = li.snagData;
    if (!data || li.snagPick) return;
    const option = data.options.find((o) => SWATCH_OPTIONS.includes(o) && this.filters.get(o)?.size === 1);
    const value = option ? [...this.filters.get(option)][0] : null;
    this.#pickLook(li, option, value, false);
  }

  #renderChips() {
    const wrap = this.querySelector('[data-chips]');
    if (!wrap) return;
    wrap.replaceChildren();
    for (const [name, set] of this.filters) {
      for (const value of set) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'snag-chip';
        chip.dataset.chip = name;
        chip.dataset.value = value;
        let text = shortLabel(value);
        if (name === 'Price') {
          text = this.querySelector(`[data-price-range="${value}"] .snag-opt__text`)?.textContent.trim() || value;
        }
        chip.setAttribute('aria-label', `Remove filter ${name}: ${text}`);
        chip.innerHTML = `<span></span><svg aria-hidden="true" width="9" height="9" viewBox="0 0 10 10"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.8"/></svg>`;
        chip.firstChild.textContent = name === 'Price' ? text : `${name}: ${text}`;
        wrap.append(chip);
      }
    }
    if (wrap.children.length) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'snag-linkbtn';
      clear.dataset.clear = '';
      clear.textContent = 'Clear all';
      wrap.append(clear);
    }
    wrap.hidden = !wrap.children.length;
  }

  /* ── sort ── */
  #initSort() {
    const select = this.querySelector('[data-sort]');
    select?.addEventListener('change', () => {
      this.sort = select.value;
      this.#render();
    });
    this.addEventListener('change', (event) => {
      const radio = event.target.closest('input[type="radio"][name^="snag-sort"]');
      if (!radio) return;
      this.sort = radio.value;
      this.#render();
    });
  }

  #applySort() {
    const select = this.querySelector('[data-sort]');
    if (select) select.value = this.sort;
    for (const radio of this.querySelectorAll('input[type="radio"][name^="snag-sort"]')) {
      radio.checked = radio.value === this.sort;
    }
    const products = this.#products();
    const key = {
      'price-asc': (a, b) => a.dataset.price - b.dataset.price,
      'price-desc': (a, b) => b.dataset.price - a.dataset.price,
      newest: (a, b) => b.dataset.created - a.dataset.created,
    }[this.sort] || ((a, b) => a.dataset.index - b.dataset.index);
    const sorted = [...products].sort(key);
    // Editorial cards keep their slot in the grid.
    const order = [];
    let p = 0;
    for (const li of this.items) order.push(li.dataset.item === 'editorial' ? li : sorted[p++]);
    order.forEach((li, i) => {
      if (this.grid.children[i] !== li) this.grid.insertBefore(li, this.grid.children[i] || null);
    });
  }

  /* ── URL: ?size=18M&character=Mario&sort=price-asc, for shareable filtered links ── */
  #readUrl() {
    const params = new URLSearchParams(window.location.search);
    for (const input of this.querySelectorAll('input[data-f]')) {
      const wanted = params.get(input.dataset.f.toLowerCase());
      if (!wanted) continue;
      const values = wanted.split(',');
      if (values.includes(shortLabel(input.value)) || values.includes(input.value)) {
        const set = this.filters.get(input.dataset.f) || new Set();
        set.add(input.value);
        this.filters.set(input.dataset.f, set);
      }
    }
    const sort = params.get('sort');
    if (['featured', 'price-asc', 'price-desc', 'newest'].includes(sort)) this.sort = sort;
  }

  #writeUrl() {
    const url = new URL(window.location.href);
    for (const input of this.querySelectorAll('input[data-f]')) url.searchParams.delete(input.dataset.f.toLowerCase());
    for (const [name, set] of this.filters) {
      const key = name.toLowerCase();
      url.searchParams.set(key, [...set].map((v) => (name === 'Price' ? v : shortLabel(v))).join(','));
    }
    if (this.sort !== 'featured') url.searchParams.set('sort', this.sort); else url.searchParams.delete('sort');
    if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url);
  }

  /* ── cards: swatches ── */
  #initCards() {
    this.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-sw]');
      if (!swatch) return;
      event.preventDefault();
      const li = swatch.closest('[data-item]');
      const same = li.snagPick && li.snagPick.value === swatch.dataset.sw;
      li.snagPick = same ? null : { option: swatch.dataset.swOption, value: swatch.dataset.sw };
      for (const s of li.querySelectorAll('[data-sw]')) s.setAttribute('aria-pressed', String(!same && s === swatch));
      if (same) this.#showFilteredLook(li);
      else this.#pickLook(li, swatch.dataset.swOption, swatch.dataset.sw, true);
    });
  }

  // Show one design on the card (or go back to the default faces).
  #pickLook(li, option, value, fromSwatch) {
    const data = li.snagData;
    const face = li.querySelector('[data-card-pick]');
    const links = li.querySelectorAll('[data-card-link]');
    if (!data) return;
    const index = option ? data.options.indexOf(option) : -1;
    const variant = index >= 0 ? (data.variants.find((v) => v.o[index] === value && v.i && v.a) || data.variants.find((v) => v.o[index] === value && v.i)) : null;
    if (face) {
      if (variant && variant.i) {
        const img = face.querySelector('img');
        if (img.getAttribute('src') !== variant.i) img.src = variant.i;
        face.hidden = false;
      } else {
        face.hidden = true;
      }
    }
    const href = variant ? `${data.url}?variant=${variant.id}` : data.url;
    links.forEach((a) => a.setAttribute('href', href));
    if (!fromSwatch) {
      for (const s of li.querySelectorAll('[data-sw]')) s.setAttribute('aria-pressed', String(Boolean(value) && s.dataset.sw === value));
    }
  }

  /* ── featured video: poster first, the clip only after the page has loaded ── */
  #initVideo() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const conn = navigator.connection || {};
    const small = window.matchMedia('(max-width: 749px)').matches || conn.saveData || /(^|-)2g|3g/.test(conn.effectiveType || '');
    const attach = (video) => {
      if (video.dataset.loaded) return;
      const src = small ? video.dataset.srcSm : video.dataset.srcMd;
      if (!src) return;
      video.dataset.loaded = 'true';
      video.src = src;
      video.muted = true;
      video.play().catch(() => {});
    };
    const afterLoad = (fn) => {
      const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 400));
      if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
    };
    for (const video of this.querySelectorAll('[data-card-video]')) {
      const play = video.parentElement.querySelector('[data-card-play]');
      if (reduce) {
        if (play) play.hidden = false;
        video.closest('a')?.addEventListener('click', (event) => {
          if (!video.dataset.loaded || video.paused) {
            event.preventDefault();
            attach(video);
            video.play().catch(() => {});
            if (play) play.hidden = true;
          }
        });
        continue;
      }
      afterLoad(() => {
        if (!('IntersectionObserver' in window)) { attach(video); return; }
        new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) { attach(video); video.play().catch(() => {}); } else if (video.dataset.loaded) video.pause();
          }
        }, { threshold: 0.2 }).observe(video);
      });
    }
  }

  /* ── quick add ── */
  #initQuickAdd() {
    this.qa = this.querySelector('[data-qa]');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quick-add]');
      if (!button) return;
      event.preventDefault();
      const li = button.closest('[data-item]');
      if (button.dataset.variantId) {
        this.#addToCart(button, [{ id: Number(button.dataset.variantId), quantity: 1 }], li.snagData);
      } else {
        this.#openQuickAdd(li, button);
      }
    });
    if (!this.qa) return;
    this.qa.querySelector('[data-qa-close]')?.addEventListener('click', () => this.qa.close());
    this.qa.addEventListener('click', (event) => {
      if (event.target === this.qa) this.qa.close(); // backdrop
    });
    this.qa.addEventListener('close', () => this.qaOpener?.focus({ preventScroll: true }));
    this.qa.querySelector('[data-qa-add]')?.addEventListener('click', () => {
      const variant = this.#qaVariant();
      if (!variant) return;
      this.#addToCart(this.qa.querySelector('[data-qa-add]'), [{ id: variant.id, quantity: 1 }], this.qaData, true);
    });
  }

  #openQuickAdd(li, opener) {
    const data = li.snagData;
    if (!data || !this.qa) return;
    this.qaData = data;
    this.qaOpener = opener;
    this.qaSel = {};
    const firstAvailable = data.variants.find((v) => v.a) || data.variants[0];
    data.options.forEach((name, i) => {
      const values = [...new Set(data.variants.map((v) => v.o[i]))];
      const filtered = this.filters.get(name);
      if (values.length === 1) this.qaSel[name] = values[0];
      else if (li.snagPick && li.snagPick.option === name) this.qaSel[name] = li.snagPick.value;
      else if (filtered && filtered.size === 1) this.qaSel[name] = [...filtered][0];
      else if (data.pre && values.includes(data.pre)) this.qaSel[name] = data.pre;
      else if (!CHOOSE_OPTIONS.includes(name)) this.qaSel[name] = firstAvailable.o[i];
      else this.qaSel[name] = null; // sizes are always the shopper's choice
    });

    this.qa.querySelector('[data-qa-title]').textContent = data.title;
    const more = this.qa.querySelector('[data-qa-link]');
    more.href = data.url;
    const note = this.qa.querySelector('[data-qa-note]');
    note.textContent = data.note || '';
    note.hidden = !data.note;
    this.qa.querySelector('[data-qa-err]').hidden = true;
    this.#renderQuickAdd();
    this.qa.showModal();
  }

  #qaVariant() {
    const data = this.qaData;
    if (!data) return null;
    if (data.options.some((name) => !this.qaSel[name])) return null;
    return data.variants.find((v) => data.options.every((name, i) => v.o[i] === this.qaSel[name])) || null;
  }

  #renderQuickAdd() {
    const data = this.qaData;
    const wrap = this.qa.querySelector('[data-qa-opts]');
    wrap.replaceChildren();

    data.options.forEach((name, i) => {
      const values = [...new Set(data.variants.map((v) => v.o[i]))];
      if (values.length < 2) return;
      const group = document.createElement('div');
      group.className = 'snag-qa__opt';
      const head = document.createElement('p');
      head.className = 'snag-qa__optname';
      head.textContent = name;
      const current = document.createElement('b');
      current.textContent = this.qaSel[name] ? shortLabel(this.qaSel[name]) : (CHOOSE_OPTIONS.includes(name) ? 'Choose one' : '');
      head.append(current);
      const row = document.createElement('div');
      row.className = 'snag-qa__vals';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', name);

      for (const value of values) {
        // Is there an in-stock variant with this value and the other current choices?
        const possible = data.variants.some((v) => v.a && v.o[i] === value && data.options.every((other, j) => j === i || !this.qaSel[other] || v.o[j] === this.qaSel[other]));
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'snag-qa__val';
        button.setAttribute('aria-pressed', String(this.qaSel[name] === value));
        button.title = value;
        const swatchImage = SWATCH_OPTIONS.includes(name) ? data.variants.find((v) => v.o[i] === value && v.i)?.i : null;
        if (swatchImage) {
          button.classList.add('snag-qa__val--img');
          if (name === 'Character') button.classList.add('snag-qa__val--hood');
          button.setAttribute('aria-label', value);
          const img = document.createElement('img');
          img.src = swatchImage.replace(/width=\d+/, 'width=120');
          img.alt = '';
          img.width = 44;
          img.height = 44;
          button.append(img);
        } else if (TIER_OPTIONS.includes(name)) {
          const tier = data.variants.find((v) => v.o[i] === value);
          button.classList.add('snag-qa__val--tier');
          const top = document.createElement('span');
          top.textContent = value;
          const sub = document.createElement('small');
          sub.textContent = formatMoney(tier.p, this.moneyFormat) + (tier.c > tier.p ? `, save ${formatMoney(tier.c - tier.p, this.moneyFormat)}` : '');
          button.append(top, sub);
        } else {
          // "18M (18–23 months)" reads as 18M with the age underneath.
          const short = shortLabel(value);
          const rest = value.slice(short.length).replace(/[()]/g, '').trim();
          const top = document.createElement('span');
          top.textContent = short;
          button.append(top);
          if (rest) {
            button.classList.add('snag-qa__val--sub');
            const sub = document.createElement('small');
            sub.textContent = rest;
            button.append(sub);
          }
        }
        if (!possible) {
          button.classList.add('is-out');
          button.setAttribute('aria-label', `${value}, sold out with this choice`);
        }
        button.addEventListener('click', () => {
          this.qaSel[name] = value;
          // A size that does not exist for the new design is cleared rather than kept wrong.
          if (!this.#qaVariant() || !this.#qaVariant().a) {
            data.options.forEach((other) => {
              if (other !== name && CHOOSE_OPTIONS.includes(other) && this.qaSel[other]) {
                const ok = data.variants.some((v) => v.a && data.options.every((o, j) => (o === other ? v.o[j] === this.qaSel[other] : !this.qaSel[o] || v.o[j] === this.qaSel[o])));
                if (!ok) this.qaSel[other] = null;
              }
            });
          }
          this.#renderQuickAdd();
        });
        row.append(button);
      }
      group.append(head, row);
      wrap.append(group);
    });

    const variant = this.#qaVariant();
    const shownVariant = variant || data.variants.find((v) => data.options.every((name, i) => !this.qaSel[name] || v.o[i] === this.qaSel[name])) || data.variants[0];
    const img = this.qa.querySelector('[data-qa-img]');
    const src = shownVariant?.i || data.image;
    if (src && img.getAttribute('src') !== src) img.src = src;
    img.alt = data.title;

    const price = this.qa.querySelector('[data-qa-price]');
    price.replaceChildren(document.createTextNode(formatMoney(shownVariant.p, this.moneyFormat)));
    if (shownVariant.c > shownVariant.p) {
      const was = document.createElement('s');
      was.textContent = formatMoney(shownVariant.c, this.moneyFormat);
      const save = document.createElement('span');
      save.className = 'snag-qa__save';
      save.textContent = `Save ${Math.round((1 - shownVariant.p / shownVariant.c) * 100)}%`;
      price.append(was, save);
    }

    const credit = this.qa.querySelector('[data-qa-credit]');
    if (this.creditPercent > 0) {
      credit.textContent = `Earn ${formatMoney(Math.floor(shownVariant.p * this.creditPercent / 100), this.moneyFormat)} store credit on this, for your next order.`;
      credit.hidden = false;
    } else {
      credit.hidden = true;
    }

    const add = this.qa.querySelector('[data-qa-add]');
    const missing = data.options.find((name) => !this.qaSel[name]);
    if (missing) {
      add.disabled = true;
      add.textContent = `Choose a ${missing.toLowerCase()}`;
    } else if (!variant || !variant.a) {
      add.disabled = true;
      add.textContent = 'Sold out in this combination';
    } else {
      add.disabled = false;
      add.textContent = `Add to cart, ${formatMoney(variant.p, this.moneyFormat)}`;
    }
  }

  // Adds through /cart/add.js and tells the theme's cart drawer, which then refreshes and opens.
  async #addToCart(button, items, data, fromDialog = false) {
    const original = button.innerHTML;
    button.classList.add('is-busy');
    button.setAttribute('aria-busy', 'true');
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

    let events = null;
    try { events = await import('@shopify/events'); } catch { events = null; }
    const Update = events?.CartLinesUpdateEvent;
    const deferred = Update ? Update.createPromise() : null;
    if (Update) {
      button.dispatchEvent(new Update({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferred.promise,
      }));
    }

    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'That could not be added. Please try again.');
      const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
      if (!Update) {
        window.location.href = `${root()}cart`;
        return;
      }
      if (fromDialog) this.qa.close();
      deferred.resolve({
        cart: Update.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'snag-collection',
          sourceId: this.dataset.sectionId,
          itemCount: items[0].quantity,
          productId: data?.id,
          sections: result.sections,
          didError: false,
        },
      });
      button.classList.add('is-added');
      if (!fromDialog) {
        button.innerHTML = '<span class="snag-card__add-label">Added</span><svg class="snag-card__add-icon" aria-hidden="true" width="12" height="10" viewBox="0 0 12 10" style="margin-left:auto"><path d="M1 5l3.5 3.5L11 1" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
      }
      window.setTimeout(() => {
        button.classList.remove('is-added');
        if (!fromDialog) button.innerHTML = original;
      }, 2200);
    } catch (error) {
      if (deferred) {
        fetch(`${root()}cart.js`).then((r) => r.json()).then((cart) => deferred.resolve({ cart: Update.createCartFromAjaxResponse(cart), detail: { didError: true } })).catch(() => deferred.resolve({ cart: null, detail: { didError: true } }));
      }
      const message = error instanceof Error ? error.message : 'That could not be added. Please try again.';
      if (fromDialog) {
        const err = this.qa.querySelector('[data-qa-err]');
        err.textContent = message;
        err.hidden = false;
      } else {
        button.innerHTML = '<span class="snag-card__add-label">Try again</span>';
        window.setTimeout(() => { button.innerHTML = original; }, 2400);
      }
    } finally {
      button.classList.remove('is-busy');
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('snag-plp')) customElements.define('snag-plp', SnagPlp);
